import { useCallback, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function makeSessionId() {
  return (
    "cabo-" +
    Math.random().toString(36).slice(2, 10) +
    "-" +
    Date.now().toString(36)
  );
}

const ERROR_COPY = {
  en: {
    stream: "Sorry, something went wrong. Please try again.",
    network: "I couldn't connect. Please check your connection and try again.",
  },
  es: {
    stream: "Lo siento, hubo un problema. Por favor intenta de nuevo.",
    network: "No pude conectarme. Verifica tu conexión e intenta de nuevo.",
  },
};

function parseSseLine(line) {
  const trimmed = line.trim();
  if (!trimmed.startsWith("data:")) return null;
  const payload = trimmed.slice(5).trim();
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch (err) {
    // Non-JSON SSE line — skip but surface during development.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("Dropping malformed SSE line:", payload);
    }
    return null;
  }
}

function updateLastAssistant(prev, updater) {
  const copy = [...prev];
  const last = copy[copy.length - 1];
  if (last && last.role === "assistant") {
    copy[copy.length - 1] = updater(last);
  }
  return copy;
}

export default function useCaboChat(initialLang) {
  const [lang, setLang] = useState(initialLang);
  const [sessionId] = useState(makeSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const toggleLang = useCallback(
    () => setLang((l) => (l === "en" ? "es" : "en")),
    []
  );

  const sendMessage = useCallback(
    async (text) => {
      const content = (text ?? input).trim();
      if (!content || streaming) return;

      const userMsg = { id: crypto.randomUUID(), role: "user", content };
      const botMsg = { id: crypto.randomUUID(), role: "assistant", content: "" };
      setMessages((m) => [...m, userMsg, botMsg]);
      setInput("");
      setStreaming(true);

      try {
        const res = await fetch(`${API}/chat/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            message: content,
            language: lang,
          }),
        });

        if (!res.ok || !res.body) {
          throw new Error(`Request failed: ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";

          for (const part of parts) {
            const evt = parseSseLine(part);
            if (!evt) continue;

            if (evt.type === "delta") {
              setMessages((prev) =>
                updateLastAssistant(prev, (last) => ({
                  ...last,
                  content: last.content + evt.content,
                }))
              );
            } else if (evt.type === "error") {
              setMessages((prev) =>
                updateLastAssistant(prev, (last) => ({
                  ...last,
                  content: ERROR_COPY[lang].stream,
                }))
              );
            }
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.error("Chat stream failed:", err);
        }
        setMessages((prev) =>
          updateLastAssistant(prev, (last) =>
            last.content ? last : { ...last, content: ERROR_COPY[lang].network }
          )
        );
      } finally {
        setStreaming(false);
      }
    },
    [input, streaming, sessionId, lang]
  );

  return {
    lang,
    toggleLang,
    sessionId,
    messages,
    input,
    setInput,
    streaming,
    sendMessage,
  };
}
